"use client";

import {useState} from "react";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/components/ui/collapsible";
import {useSyncCollapsibleOpen} from "@/hooks/useSyncCollapsibleOpen";
import StepDetailContent from "./code-execution-step/StepDetailContent";
import StepRow from "./code-execution-step/StepRow";
import {type CodeExecutionStepDetail, normalizeWhitespace} from "./toolCallUtils";

/**
 * Chip shows the first line of what ran — unless the row title already is
 * that line (summaries fall back to a truncated code line) or the payload is
 * a raw JSON parameter dump, in which case the language badge reads better.
 */
const getChipText = (detail: CodeExecutionStepDetail): string | null => {
   if (!detail.code) return null;
   if (detail.codeLanguage === "json") return detail.badgeText;

   const line = normalizeWhitespace(detail.code.split("\n")[0] ?? "");
   if (!line) return detail.badgeText;

   const titleIsCode =
      line === detail.title ||
      (detail.title.endsWith("…") && line.startsWith(detail.title.slice(0, -1)));
   return titleIsCode ? detail.badgeText : line;
};

type CodeExecutionToolStepProps = Readonly<{
   detail: CodeExecutionStepDetail;
   isCompleted: boolean;
   /** A single-step group already uses its summary as the trigger. */
   contentOnly?: boolean;
}>;

export default function CodeExecutionToolStep({
   detail,
   isCompleted,
   contentOnly = false,
}: CodeExecutionToolStepProps) {
   const completed = detail.completed || isCompleted;
   const showCode = Boolean(detail.code);
   const expandable = showCode || Boolean(detail.output);
   // Live and failed steps stay open; successful completions collapse.
   const [open, setOpen] = useSyncCollapsibleOpen(!completed || detail.failed);
   const [showAllCode, setShowAllCode] = useState(false);
   const [showAllOutput, setShowAllOutput] = useState(false);
   const chipText = getChipText(detail);

   const detailContent = (
      <StepDetailContent
         detail={detail}
         contentOnly={contentOnly}
         showAllCode={showAllCode}
         onToggleAllCode={() => setShowAllCode((current) => !current)}
         showAllOutput={showAllOutput}
         onToggleAllOutput={() => setShowAllOutput((current) => !current)}
      />
   );

   if (contentOnly) {
      return <div className="pt-0.5">{detailContent}</div>;
   }

   const row = (
      <StepRow
         expandable={expandable}
         open={open}
         completed={completed}
         failed={detail.failed}
         title={detail.title}
         chipText={chipText}
         chipHidden={open && showCode}
      />
   );

   if (!expandable) {
      return <div className="flex w-full items-center gap-2 py-1">{row}</div>;
   }

   return (
      <Collapsible
         open={open}
         onOpenChange={setOpen}
         className="transition-colors duration-150"
      >
         <CollapsibleTrigger className="-ml-1 flex w-[calc(100%+0.25rem)] items-center gap-2 rounded-md px-1 py-1 text-left transition-colors duration-100 hover:bg-neutral/30">
            {row}
         </CollapsibleTrigger>
         <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0">
            {detailContent}
         </CollapsibleContent>
      </Collapsible>
   );
}
